import React from "react";
import { Sprout, RotateCcw, LogOut, User as UserIcon, Gamepad2, Users, Award, Target, ShieldAlert, UsersRound, UserRound } from "lucide-react";
import { fmt } from "../../lib/game";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const ProgressRing = ({ round, total }) => {
  const size = 40;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(round, total) / total;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="round-progress-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.1)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#10B981" strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold font-mono text-emerald-400">
        {Math.min(round, total)}
      </span>
    </div>
  );
};

export const Header = ({ session, totalRounds = 8, onLearnMore, onReset, onHome, onMyGames, onFacilitator, onMyProgress, onMyCommitments, onMyProfile, onAdmin, onPlayTogether, onSignIn }) => {
  const { user, logout } = useAuth();
  const loggedIn = user && typeof user === "object";

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/80 px-4 lg:px-8 py-3 flex items-center justify-between">
      <button className="flex items-center gap-2.5" onClick={onHome} data-testid="brand-home-btn">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
          <Sprout className="w-5 h-5 text-slate-950" strokeWidth={2.4} />
        </div>
        <span className="font-heading text-xl font-bold tracking-tight text-slate-100">Burgeon</span>
      </button>

      <div className="flex items-center gap-2 sm:gap-3">
        {session && (
          <>
            <div className="flex items-center gap-2" data-testid="round-tracker">
              <ProgressRing round={session.current_round} total={totalRounds} />
              <div className="hidden sm:block leading-tight">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Round</p>
                <p className="text-sm font-bold text-slate-200 font-mono">
                  {Math.min(session.current_round, totalRounds)} of {totalRounds}
                </p>
              </div>
            </div>
            <div className="hidden sm:block px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 leading-tight" data-testid="header-net-worth">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Net Worth</p>
              <p className="text-sm font-bold text-emerald-400 font-mono">{fmt(session.net_worth)}</p>
            </div>
            {onReset && (
              <Button variant="ghost" size="icon" onClick={onReset} data-testid="reset-session-btn"
                className="text-slate-400 hover:text-white hover:bg-slate-800" title="Start over">
                <RotateCcw className="w-4 h-4" />
              </Button>
            )}
          </>
        )}

        <Button variant="ghost" size="sm" onClick={onPlayTogether} data-testid="nav-play-together-btn"
          className="hidden sm:inline-flex text-slate-300 hover:text-white hover:bg-slate-800">
          <UsersRound className="w-4 h-4 mr-1.5 text-pink-400" /> Play Together
        </Button>

        <Button variant="ghost" size="sm" onClick={onLearnMore} data-testid="learn-more-btn"
          className="hidden sm:inline-flex text-slate-300 hover:text-white hover:bg-slate-800">
          Learn More
        </Button>

        {loggedIn ? (
          <>
            <button
              data-testid="streak-indicator"
              onClick={onMyProgress}
              title="Your daily streak"
              className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
            >
              <Sprout className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-bold font-mono text-emerald-300">{user.current_streak ?? 0}</span>
            </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                ) : (
                  <span className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-xs font-bold">
                    {(user.name || user.email || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="hidden sm:block text-sm font-medium text-slate-200 max-w-[110px] truncate">{user.name || user.email}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 text-slate-200 w-52">
              <DropdownMenuLabel className="text-slate-400 font-normal text-xs truncate">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem data-testid="nav-my-profile" onClick={onMyProfile} className="focus:bg-slate-800 cursor-pointer">
                <UserRound className="w-4 h-4 mr-2" /> My profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="nav-my-games" onClick={onMyGames} className="focus:bg-slate-800 cursor-pointer">
                <Gamepad2 className="w-4 h-4 mr-2" /> My Games
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="nav-my-progress" onClick={onMyProgress} className="focus:bg-slate-800 cursor-pointer">
                <Award className="w-4 h-4 mr-2" /> My Progress
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="nav-my-commitments" onClick={onMyCommitments} className="focus:bg-slate-800 cursor-pointer">
                <Target className="w-4 h-4 mr-2" /> My Commitments
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="nav-play-together" onClick={onPlayTogether} className="focus:bg-slate-800 cursor-pointer">
                <UsersRound className="w-4 h-4 mr-2" /> Play Together
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="nav-facilitator" onClick={onFacilitator} className="focus:bg-slate-800 cursor-pointer">
                <Users className="w-4 h-4 mr-2" /> Facilitator Mode
              </DropdownMenuItem>
              {user.role === "admin" && (
                <DropdownMenuItem data-testid="nav-admin" onClick={onAdmin} className="focus:bg-slate-800 cursor-pointer text-emerald-300">
                  <ShieldAlert className="w-4 h-4 mr-2" /> Admin Dashboard
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="focus:bg-slate-800 cursor-pointer text-red-300">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </>
        ) : (
          <Button size="sm" onClick={onSignIn} data-testid="sign-in-btn"
            className="bg-slate-100 text-slate-900 hover:bg-white font-semibold">
            <UserIcon className="w-4 h-4 mr-1.5" /> Sign In
          </Button>
        )}
      </div>
    </header>
  );
};
